import { AutoTranslate } from "./AutoTranslate";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { supportFeedback } from "../lib/api";
import { Button } from "./ui/button";

const MOODS = [["confusing", "Something's confusing"], ["broken", "Something broke"], ["idea", "I have an idea"], ["love", "This works well"]];

// Private-beta feedback: one tap from any vault page, lands in the Administrator's support inbox tagged with the page.
export const BetaFeedback = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = (e) => e.detail === "feedback" && setOpen(true);
    window.addEventListener("hv:open", onOpen);
    return () => window.removeEventListener("hv:open", onOpen);
  }, []);
  const [mood, setMood] = useState("confusing");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 5) { toast.error("Tell us a little more — a sentence is plenty"); return; }
    setBusy(true);
    try {
      await supportFeedback({ text: text.trim(), page: pathname, mood });
      toast.success("Thank you — that went straight to the team");
      setText(""); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send that — try again"); } finally { setBusy(false); }
  };
  return (
    <>
      <button style={{ display: "none" }} onClick={() => setOpen(true)} className="fixed bottom-20 right-4 z-40 inline-flex items-center gap-1.5 rounded-full border border-border bg-card/95 px-3 py-1.5 text-xs shadow-md backdrop-blur hover:border-primary hover:text-primary print:hidden" data-testid="beta-feedback-btn" aria-label="Send feedback">
        <MessageSquareText className="h-3.5 w-3.5" /> Feedback
      </button>
      {open && (
        <AutoTranslate><div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-black/30 p-3" onClick={() => setOpen(false)} data-testid="beta-feedback-overlay">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-4 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="beta-feedback-sheet">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <h3 className="font-medium">Tell us what's confusing</h3>
                <p className="text-xs text-muted-foreground mt-0.5">You're helping shape HearthVault. We read every note; we'll reply if we need more.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="beta-feedback-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {MOODS.map(([v, l]) => <button key={v} onClick={() => setMood(v)} className={`rounded-full border px-2.5 py-1 text-xs ${mood === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`} data-testid={`beta-feedback-mood-${v}`}>{l}</button>)}
            </div>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="What were you trying to do, and what happened?" className="mt-3 w-full rounded-lg border border-border bg-background p-2.5 text-sm" data-testid="beta-feedback-text" />
            <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="truncate">Page: {pathname}</span>
              <Button size="sm" className="ml-auto rounded-full h-8" onClick={send} disabled={busy} data-testid="beta-feedback-send">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send"}</Button>
            </div>
          </div>
        </div></AutoTranslate>
      )}
    </>
  );
};
